import React, { useState } from 'react';
import { companyInfo } from '../mock';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success(response.data.message);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            inquiryType: 'general',
            message: ''
          });
          setSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      style={{
        background: '#000000',
        padding: '100px 7.6923%',
        position: 'relative'
      }}
    >
      <div 
        style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}
      >
        <div 
          style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}
        >
          <h2 
            className="display-large"
            style={{
              marginBottom: '20px'
            }}
          >
            Contact Us
          </h2>
          <div 
            style={{
              width: '60px',
              height: '3px',
              background: '#00FFD1',
              margin: '0 auto 24px'
            }}
          />
          <p 
            className="body-large"
            style={{
              color: 'rgba(255, 255, 255, 0.85)',
              margin: 0
            }}
          >
            Get in touch for a consultation or to learn more about our services
          </p>
        </div>
        
        <div 
          style={{
            background: '#121212',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            padding: '60px',
            borderRadius: '0px'
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Name */}
              <div>
                <Label 
                  htmlFor="name"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#FFFFFF',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  style={{
                    background: '#000000',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    color: '#FFFFFF',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '0px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00FFD1';
                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 255, 209, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Email */}
              <div>
                <Label 
                  htmlFor="email"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#FFFFFF',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  style={{
                    background: '#000000',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    color: '#FFFFFF',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '0px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00FFD1';
                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 255, 209, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Inquiry Type */}
              <div>
                <Label 
                  htmlFor="inquiryType"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#FFFFFF',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Inquiry Type
                </Label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    background: '#000000',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    color: '#FFFFFF',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '0px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00FFD1';
                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 255, 209, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="ai-landing-page">AI Landing Page</option>
                  <option value="ai-website">AI Website Building</option>
                  <option value="business-process">Business Process Analysis</option>
                  <option value="flowmingo">Flowmingo AI Recruiter Questions</option>
                  <option value="general">General Request / Quote</option>
                  <option value="language-services">Language Services</option>
                  <option value="voice-over">Voice Over Request</option>
                </select>
              </div>
              
              {/* Message */}
              <div>
                <Label 
                  htmlFor="message"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#FFFFFF',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us about your needs..."
                  rows={6}
                  style={{
                    background: '#000000',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    color: '#FFFFFF',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '0px',
                    transition: 'all 0.3s ease',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00FFD1';
                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 255, 209, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || submitted}
                className="btn-primary"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  opacity: (isSubmitting || submitted) ? 0.7 : 1,
                  cursor: (isSubmitting || submitted) ? 'not-allowed' : 'pointer'
                }}
              >
                {submitted ? (
                  <>
                    <CheckCircle size={20} />
                    Message Sent
                  </>
                ) : isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
          
          {/* Message sent confirmation - no email display */}
          <div style={{ display: 'none' }}>
            {/* Email info removed for privacy */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
